import React from 'react';
import { Plus, X } from 'lucide-react';

const PartnerFormContent = ({ 
  formData, 
  onInputChange, 
  showAdvancedSettings, 
  onToggleAdvanced,
  categories = [],
  compact = false,
  theme = 'dark' // 'dark' or 'light'
}) => {
  // 處理回應範例的新增
  const handleAddResponseExample = () => {
    const newExamples = [...(formData.responseExamples || ['']), ''];
    onInputChange({ 
      target: { 
        name: 'responseExamples', 
        value: newExamples 
      } 
    });
  };

  // 處理回應範例的刪除
  const handleRemoveResponseExample = (index) => {
    const newExamples = formData.responseExamples.filter((_, i) => i !== index);
    onInputChange({ 
      target: { 
        name: 'responseExamples', 
        value: newExamples 
      } 
    });
  };

  // 處理回應範例的修改
  const handleResponseExampleChange = (index, value) => {
    const newExamples = formData.responseExamples.map((item, i) => i === index ? value : item);
    onInputChange({ 
      target: { 
        name: 'responseExamples', 
        value: newExamples 
      } 
    });
  };

  const defaultCategories = [
    { value: 'general', label: '通用教學' },
    { value: 'science', label: '科學' },
    { value: 'math', label: '數學' },
    { value: 'language', label: '語言' },
    { value: 'programming', label: '程式設計' },
    { value: 'art', label: '藝術' },
    { value: 'music', label: '音樂' },
    { value: 'sports', label: '體育' },
    { value: 'other', label: '其他' }
  ];

  const categoryOptions = categories.length > 0 
    ? categories.filter(cat => cat.value !== 'all')
    : defaultCategories;

  return (
    <div className={`partner-form-content ${compact ? 'compact' : ''} ${theme === 'light' ? 'light-theme' : 'dark-theme'}`}>
      {/* 基礎必填欄位 */}
      <div className="form-group">
        <label>名稱 *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onInputChange}
          placeholder="例如：學習小幫手"
          required
        />
      </div>

      <div className="form-group">
        <label>描述</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={onInputChange}
          rows="3"
          placeholder="描述這個夥伴的用途和特點"
        />
      </div>

      {/* 基礎設定 */}
      <div className="form-section basic-settings">
        <h4>基礎設定</h4>
        
        <div className="settings-group">
          <h5>分類與標籤</h5>
          <div className="form-group">
            <label>分類</label>
            <select
              name="category"
              value={formData.category}
              onChange={onInputChange}
            >
              {categoryOptions.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>標籤 (以逗號分隔)</label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={onInputChange}
              placeholder="例：AI、教育、數學"
            />
          </div>
        </div>

        {/* 角色任務與行動原則 */}
        <div className="settings-group">
          <h5>角色任務與行動原則</h5>
          <div className="form-group">
            <label>首要任務</label>
            <input
              type="text"
              name="primaryTask"
              value={formData.primaryTask}
              onChange={onInputChange}
              placeholder="例：在私訊中協助使用者進行對話和思考"
            />
          </div>
          <div className="form-group">
            <label>核心動機</label>
            <textarea
              name="coreMotivation"
              value={formData.coreMotivation}
              onChange={onInputChange}
              rows="2"
              placeholder="例：追求實用、高效、創新的技術解決方案"
            />
          </div>
          <div className="form-group">
            <label>開場方式</label>
            <input
              type="text"
              name="openingApproach"
              value={formData.openingApproach}
              onChange={onInputChange}
              placeholder="例：你好！我是你的學習夥伴"
            />
          </div>
          <div className="form-group">
            <label>回應範例</label>
            {formData.responseExamples && formData.responseExamples.map((example, index) => (
              <div key={index} className="response-example-item">
                <input
                  type="text"
                  value={example}
                  onChange={(e) => handleResponseExampleChange(index, e.target.value)}
                  placeholder={`範例 ${index + 1}`}
                />
                {formData.responseExamples.length > 1 && (
                  <button
                    type="button"
                    className="remove-example-btn"
                    onClick={() => handleRemoveResponseExample(index)}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="add-example-btn"
              onClick={handleAddResponseExample}
            >
              <Plus size={16} />
              新增回應範例
            </button>
          </div>
        </div>

        {/* 知識庫與專業 */}
        <div className="settings-group">
          <h5>知識庫與專業</h5>
          <div className="form-group">
            <label>專業技能</label>
            <textarea
              name="professionalSkills"
              value={formData.professionalSkills}
              onChange={onInputChange}
              rows="2"
              placeholder="例：軟體開發、問題分析、方案設計"
            />
          </div>
          <div className="form-group">
            <label>背景知識</label>
            <textarea
              name="backgroundKnowledge"
              value={formData.backgroundKnowledge}
              onChange={onInputChange}
              rows="3"
              placeholder="描述夥伴的背景知識領域"
            />
          </div>
          <div className="form-group">
            <label>關鍵術語 (每行一個)</label>
            <textarea
              name="keyTerminology"
              value={formData.keyTerminology}
              onChange={onInputChange}
              rows="3"
              placeholder="例：&#10;API&#10;資料庫&#10;演算法"
            />
          </div>
        </div>

        {/* 行為與溝通風格 */}
        <div className="settings-group">
          <h5>行為與溝通風格</h5>
          <div className="form-group">
            <label>性格特質</label>
            <input
              type="text"
              name="personality"
              value={formData.personality}
              onChange={onInputChange}
              placeholder="例：友善、耐心、支持性"
            />
          </div>
          <div className="form-group">
            <label>溝通風格</label>
            <input
              type="text"
              name="communicationStyleDetail"
              value={formData.communicationStyleDetail}
              onChange={onInputChange}
              placeholder="例：溫和、鼓勵式"
            />
          </div>
          <div className="form-group">
            <label>互動方式</label>
            <textarea
              name="interactionMethod"
              value={formData.interactionMethod}
              onChange={onInputChange}
              rows="2"
              placeholder="例：使用問題導向為主的對話框架"
            />
          </div>
          <div className="form-group">
            <label>行為準則 (每行一個)</label>
            <textarea
              name="guardrails"
              value={formData.guardrails}
              onChange={onInputChange}
              rows="3"
              placeholder="例：&#10;不提供完整答案&#10;鼓勵學生思考&#10;保持中立態度"
            />
          </div>
        </div>
      </div>

      {/* 進階設定 */}
      {onToggleAdvanced && (
        <div className="advanced-settings-toggle">
          <button
            type="button"
            className="toggle-advanced-btn"
            onClick={onToggleAdvanced}
          >
            {showAdvancedSettings ? '隱藏' : '顯示'}進階設定
          </button>
        </div>
      )}

      {showAdvancedSettings && (
        <div className="form-section advanced-settings">
          <h4>進階設定</h4>
          
          <div className="settings-group">
            <h5>回應時機</h5>
            <div className="form-group">
              <label>回應模式</label>
              <select
                name="responseMode"
                value={formData.responseMode}
                onChange={onInputChange}
              >
                <option value="always">總是回應</option>
                <option value="mentioned">被提及時回應</option>
                <option value="conditional">條件式回應</option>
                <option value="scheduled">按時程回應</option>
              </select>
            </div>
            <div className="form-group">
              <label>回應條件</label>
              <textarea
                name="responseConditions"
                value={formData.responseConditions}
                onChange={onInputChange}
                rows="2"
                placeholder="描述何時應該回應"
              />
            </div>
            <div className="form-group">
              <label>可用時段</label>
              <input
                type="text"
                name="availabilitySchedule"
                value={formData.availabilitySchedule}
                onChange={onInputChange}
                placeholder="例：週一至週五 9:00-17:00"
              />
            </div>
            <div className="form-group">
              <label>回應延遲 (秒)</label>
              <input
                type="text"
                name="responseDelay"
                value={formData.responseDelay}
                onChange={onInputChange}
                placeholder="例：5"
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isPublicTemplate"
                checked={formData.isPublicTemplate}
                onChange={onInputChange}
              />
              設為公開模板 (其他用戶可以複製使用)
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerFormContent;
